import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { ProductTypes } from 'common/src/modules/products/product-types';
import { BaseFormComponent } from 'projects/workspace/src/app/crm/partner-forms/components/base-form.component';
import { selectCompanyProfile } from 'projects/workspace/src/app/administration/store/selectors';
import { CompanyProfile } from 'projects/workspace/src/app/administration/models/company-profile.model';
import { AppState } from 'projects/workspace/src/app/store/state/app.state';
import { TradePricesApiService } from 'projects/workspace/src/app/trade-prices/services/trade-prices-api.service';
import { MetricConversionPipe } from '../../../rnpl-common';


@Component({
  selector: 'rnpl-create-product-form-price',
  templateUrl: './create-product-form-price.component.html'
})
export class CreateProductFormPriceComponent extends BaseFormComponent implements OnInit, OnChanges, OnDestroy {

  public localCurrency: string = '€';
  public companyProfile: CompanyProfile;
  public productTypes = ProductTypes;

  @Input() productType: ProductTypes;

  @Output() calcBundlePricesEmit: EventEmitter<null> = new EventEmitter<null>();

  readonly isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private destroy$: ReplaySubject<any> = new ReplaySubject<any>(1);

  constructor(
    private fb: FormBuilder,
    private tradePricesApiService: TradePricesApiService,
    private metricPipe: MetricConversionPipe,
    private readonly store: Store<AppState>,
  ) {
    super();

    this.store.select(selectCompanyProfile)
      .pipe(takeUntil(this.destroy$))
      .subscribe((companyProfile: CompanyProfile) => {
        this.companyProfile = companyProfile;
        this.applySmallBusinessVatSettings();
      });
  }

  ngOnInit(): void {
    this.initForm();
    this.updateFormEditingStatus();
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    if (changes.hasOwnProperty('readonly')) {
      setTimeout(() => this.applySmallBusinessVatSettings());
    }
  }

  public initForm(): void {
    this.form = this.fb.group({
      netPrice: [],
      vat: [this.isSmallBusiness ? 0 : null],
      grossPrice: [],
      priceFromBundledProducts: [false],
    });

    this.priceFromBundledProductsControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: boolean) => {
        if (value) {
          this.netPriceControl.disable();
          this.grossPriceControl.disable();
        } else {
          if (!this.readonly) {
            this.netPriceControl.enable();
            this.grossPriceControl.enable();
          }
        }
      });

    this.applySmallBusinessVatSettings();
  }

  public calcBundlePrices(): void {
    this.calcBundlePricesEmit.emit();
  }

  public getFormData(): any {
    return {
      netPrice: this.toInteger(this.netPriceControl.value),
      vat: this.isSmallBusiness ? 0 : this.vatControl.value,
      grossPrice: this.toInteger(this.grossPriceControl.value),
      priceFromBundledProducts: this.priceFromBundledProductsValue
    };
  }

  private toInteger(value: number | string): number {
    if (value === 0) { return value; }
    if (!value) { return null; }
    return +this.metricPipe.transform(+value, 'TO-INTEGER').toFixed(0);
  }

  private fillControlValueWithConversion(value: number | string): number {
    if (value === 0) { return value; }
    if (!value) { return null; }
    return this.metricPipe.transform(+value, 'TO-FRACTIONAL') || null;
  }

  public updateField(fieldName: string, formData = this.getFormData()): void {
    this.tradePricesApiService.calcPrice(fieldName, formData)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.vatControl.patchValue(this.isSmallBusiness ? 0 : res.vat);
        this.netPriceControl.patchValue(this.fillControlValueWithConversion(res.netPrice));
        this.grossPriceControl.patchValue(this.fillControlValueWithConversion(res.grossPrice));
        this.applySmallBusinessVatSettings();
      });
  }

  public updateFormEditingStatus(): void {
    super.updateFormEditingStatus();
    setTimeout(() => this.applySmallBusinessVatSettings());
  }

  private applySmallBusinessVatSettings(): void {
    if (!this.form) { return; }

    if (this.isSmallBusiness) {
      this.vatControl.patchValue(0, { emitEvent: false });
      this.vatControl.disable({ emitEvent: false });
      return;
    }

    if (!this.readonly) {
      this.vatControl.enable({ emitEvent: false });
    }
  }

  get priceFromBundledProductsValue(): boolean {
    return this.priceFromBundledProductsControl.value;
  }

  get vatValue(): number {
    return this.isSmallBusiness ? 0 : this.vatControl.value;
  }

  get isSmallBusiness(): boolean {
    return !!(this.companyProfile && this.companyProfile.isSmallBusiness);
  }

  get netPriceControl(): FormControl { return this.form.get('netPrice') as FormControl; }
  get vatControl(): FormControl { return this.form.get('vat') as FormControl; }
  get grossPriceControl(): FormControl { return this.form.get('grossPrice') as FormControl; }
  get priceFromBundledProductsControl(): FormControl { return this.form.get('priceFromBundledProducts') as FormControl; }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

}
